/**
 * Raymarched volumetric cumulus.
 *
 * ## Shape
 *
 * The layer is a **spherical shell** around the planet, not a flat slab, and that is not
 * pedantry — it is the difference between clouds that converge to a line at the horizon (which
 * is what they do) and clouds that stretch to infinity in a flat plane (which is what a slab
 * gives you, and which instantly reads as a ceiling). At 4000 ft the shell's horizon is about
 * 70 miles away, and the layer visibly closing up toward it is most of what sells the scale.
 *
 * Density comes from `cloudNoise.ts`: a Perlin–Worley base eroded by successively finer Worley
 * FBM, shaped vertically by a height gradient and horizontally by a low-frequency coverage
 * field, so cloud clumps into weather rather than spreading evenly.
 *
 * ## The three things that make raymarched cloud affordable
 *
 * A naive implementation is 128 samples per pixel of a 3D texture, per frame, over most of the
 * screen. That is not a 60 fps effect on an integrated GPU. Three things bring it into budget,
 * and all three are required — dropping any one puts it back out:
 *
 * 1. **Half resolution.** A quarter of the pixels. Clouds are low-frequency, so the only thing
 *    lost is the silhouette edge, which the upsample handles.
 * 2. **Blue-noise jitter.** Starting every ray at the same offset makes the step size visible as
 *    concentric banding. Offsetting each ray by a per-pixel dither turns that banding into
 *    high-frequency noise — which is *far* less objectionable to the eye, and which the next
 *    step then removes. This is what allows 48 steps to look like 300.
 * 3. **Temporal reprojection.** The jitter pattern rotates each frame, and each frame is blended
 *    with the previous one reprojected into the current view. Ten frames of different noise
 *    average to a clean image, so the effective sample count is ten times the real one.
 *
 * History is rejected when the reprojected sample falls off screen, which is what stops the
 * layer smearing when the aircraft rolls.
 *
 * ## Why this pass renders itself instead of being a node
 *
 * This was first written as a `TempNode` that rendered from `updateBefore` and handed the post
 * chain a `passTexture()` — the arrangement three's own `TRAANode` uses. It never drew anything,
 * and the reason is worth recording because nothing about it is visible from the outside:
 * **three never built the node at all.** `setup()` was called exactly zero times, so the node was
 * never registered as a sequential node, so `updateBefore` never ran, so the render target was
 * never even resized off its initial 1×1 — and none of that raises an error, warns, or fails a
 * compile. The composite dutifully sampled a texture that no one had ever written.
 *
 * The trigger is nesting: the cloud composite feeds `traa()`, which wraps its input in
 * `convertToTexture()` — an `RTTNode` that recompiles the subgraph into a second material — and
 * the custom pass does not survive that round trip. Referencing the pass node directly instead of
 * its texture node (which is the difference between this and `TRAANode`, and the obvious fix)
 * changes nothing.
 *
 * So the pass owns its own rendering, end to end, and the only thing it hands the post chain is a
 * plain `texture()` — a value, with no lifecycle for three to drop:
 *
 * - two render targets it sizes and clears itself,
 * - its own `QuadMesh` and `NodeMaterial`,
 * - **every** camera and scene input as an explicit uniform pushed once per frame — never the TSL
 *   camera globals, which inside a quad-rendered material describe the quad's orthographic camera
 *   and not the aeroplane's,
 * - {@link VolumetricClouds.render} called by hand from the frame loop, after the scene has been
 *   drawn (its depth buffer is an input) and before the post chain composites.
 *
 * History rolls forward with `copyTextureToTexture` rather than by swapping the two targets.
 * Swapping is one fewer blit, but it means the texture the composite samples changes identity
 * every frame, and a stale handle there is precisely the silent, invisible failure this pass has
 * already cost a milestone to. A half-resolution RGBA16F blit is about 0.1 ms; certainty is worth
 * more than that.
 *
 * ## What was actually wrong, and how it was found
 *
 * Three attempts at this pass produced no cloud at all. Every attempt found and fixed a real bug —
 * the node that three never built, the uniform loop bound, the texture-coordinate precision
 * collapse, the alpha that `setupDiffuseColor` overwrote — and after every one the buffer still
 * came back transmittance 1.0, in-scattering 0, on all 518 400 pixels.
 *
 * **The cause was a vertical flip**, and it is worth stating the mechanism correctly because a
 * *nearly* correct version of it caused the same bug a second time.
 *
 * This pass reconstructs its view ray as `ndc = screenUV·2 − 1` while `screenUV.y` is top-down, so
 * the ray stored at buffer row `v` belongs to screen position `1 − v`: **its buffer is stored
 * vertically mirrored.** The scene depth was sampled without accounting for that, so every ray read
 * the depth of the pixel reflected about the middle of the screen — upward rays, which see sky, read
 * the *ground's* depth of a few metres, and `end` clamped below `start`. The march was skipped on
 * every upward ray of every frame.
 *
 * The mirror is a property of **this pass**, not of a backend. Both of the coordinate spaces
 * involved are normalised by three itself — `ScreenNode` flips the fragment coordinate on GLSL "to
 * follow webgpu standards", and `TextureNode` flips the sampling uv on GLSL for render-target and
 * depth textures — so the correction is the same on WebGPU and WebGL2. Getting that wrong is
 * exactly what M7 part 3 had to undo: the M3 fix wrote `flipScreenSpaceY: backend !== 'webgl2'`,
 * measured on one backend and assumed on the other, and WebGL2 rendered no cloud for two
 * milestones. See {@link VolumetricClouds.screenFlipY}, which carries both measurements.
 *
 * Why it survived three attempts is the part worth keeping:
 *
 * - **Nothing errors.** No validation failure, no warning, no NaN. Every intermediate value in the
 *   shader is correct; they are just combined with the wrong pixel's depth.
 * - **The symptom is indistinguishable from three other states.** A buffer of `rgb = 0, a = 1` is
 *   what a skipped march writes, what a march through empty density writes, *and* what the renderer
 *   clears the target to (`getClearAlpha()` is 1). "The pass produced nothing" and "the pass never
 *   ran" look identical from outside.
 * - **It hides from any probe without terrain in the depth buffer.** With an empty depth attachment
 *   every fetch returns 1.0, the flip cannot matter, and the intervals, the density and the step
 *   counts all measure healthy. Several of the readings recorded here across earlier attempts were
 *   taken from a page whose render loop had stalled — a backgrounded tab gets no animation frames —
 *   and so measured exactly that.
 *
 * What broke the deadlock was {@link VolumetricClouds.probeInternals}: a second material, sharing
 * this one's uniforms and node code, that writes the march's *intermediates* instead of its result.
 * It showed a valid interval and a density of 0.88 on all 518 400 pixels in the same frame the
 * shipped buffer was empty, which ruled out the geometry, the volumes and the density function in
 * one measurement; then, evaluating both depth coordinates side by side in a single shader
 * invocation, it showed **0** march steps for `screenUV` and up to 64 for its mirror — and 0
 * exactly where terrain occludes. It is still here, still evaluating both, and it is the first
 * thing to run if this layer ever goes quiet again.
 *
 * Two more defects were found on the way and are fixed, both pinned by `shellInterval.test.ts`
 * against an f32-faithful CPU port of the shell algebra: `sign(0)` collapsing the root pair for a
 * level ray, and a grazing ray from above the layer marching half its chord. Neither could have
 * caused this symptom, which is what sent the search to the pass's inputs.
 *
 * Temporal reprojection is **on** from M7 part 1, on every tier. It was off for a milestone with a
 * residual defect of its own; both halves of that defect are described at the reprojection itself
 * and pinned by `cloudReprojection.test.ts`.
 *
 * ## Lighting
 *
 * Beer's law along a short march toward the sun for self-shadowing, Henyey–Greenstein phase for
 * the forward scattering that makes a cloud glow when you fly toward the sun, and the
 * **powder-sugar** term — an approximation of the fact that light entering a dense medium
 * scatters back out near the entry point, which makes cloud edges facing the sun *darker* than
 * their interiors. It is counter-intuitive and it is the difference between cloud that looks
 * like a solid and cloud that looks like a volume.
 *
 * `updateFrame(alpha)`: yes — {@link VolumetricClouds.updateFrame} advects the field, pushes the
 * weather and updates the in-cloud immersion term. {@link VolumetricClouds.render} is called
 * separately, once per frame, from the render step.
 */

import {
  Data3DTexture,
  HalfFloatType,
  LinearFilter,
  Matrix4,
  NoBlending,
  RGBAFormat,
  RedFormat,
  RenderTarget,
  RepeatWrapping,
  Vector2,
  Vector3,
  type DepthTexture,
  type PerspectiveCamera,
} from 'three';
import {
  NodeMaterial,
  QuadMesh,
  RendererUtils,
  type Node,
  type Renderer,
} from 'three/webgpu';
import {
  Fn,
  Loop,
  If,
  clamp,
  dot,
  exp,
  float,
  max,
  min,
  mix,
  normalize,
  perspectiveDepthToViewZ,
  pow,
  screenUV,
  smoothstep,
  sqrt,
  texture,
  texture3D,
  uniform,
  uv,
  vec2,
  vec3,
  vec4,
} from 'three/tsl';
import type { QualitySettings } from '../../core/quality.js';
import type { WeatherState } from '../../world/weather.js';
import { generateCloudNoise, remap, sampleVolume } from './cloudNoise.js';
import type { CloudNoiseResponse } from './cloudNoise.worker.js';
import { CloudShadow } from './cloudShadow.js';
import {
  DENSITY_BAND,
  coverageOpacity,
  coverageOpacityNode,
  coverageThreshold,
  coverageThresholdNode,
} from './coverage.js';

/**
 * Shipped volume sizes.
 *
 * 64³ costs ~700 ms to generate and 1 MB of VRAM. 32³ is four times faster and visibly blockier
 * — the base billows stop having billows of their own — and 96³ is 2.2 s, which is longer than
 * the whole rest of start-up for detail that the detail volume is already providing.
 */
const SHAPE_SIZE = 64;
const DETAIL_SIZE = 32;

/** Planet radius for the shell [m]. Matches the world's mean radius. */
const EARTH_RADIUS_M = 6_371_008.8;

/**
 * Compile-time raymarch bound. The tier's `raymarchSteps` sets the step *length* against this;
 * see the loop for why the bound cannot be a uniform.
 */
const MAX_RAYMARCH_STEPS = 64;
/** Steps of the shadow march toward the sun. */
const LIGHT_STEPS = 6;

/**
 * Horizontal repeat of the shape volume: one repeat per 9 km.
 *
 * Module constants because two shaders sample the volume — the march and the geometry diagnostic —
 * and they have to agree. See {@link VolumetricClouds.sampleDensity} for why the vertical axis is
 * driven by the height fraction rather than by the position, which is a precision bug this file has
 * already paid for once.
 */
const SHAPE_SAMPLE_SCALE = 1 / 9_000;
/** Fraction of the shape volume's vertical axis the cloud layer spans. */
const SHAPE_VERTICAL_SPAN = 0.85;

const quad = /* #__PURE__ */ new QuadMesh();
const scratchMatrix = /* #__PURE__ */ new Matrix4();
const scratchSize = /* #__PURE__ */ new Vector2();
/**
 * Reused across frames, as three's own passes do, so the frame path allocates nothing.
 *
 * `resetRendererState` both fills and returns the object, and tolerates being handed nothing the
 * first time; the cast is only to say so, because its parameter is typed as required.
 */
let rendererState = undefined as unknown as ReturnType<typeof RendererUtils.resetRendererState>;

/**
 * The 4×4 ordered dither used for the per-ray offset.
 *
 * Bayer rather than true blue noise: a 4×4 Bayer matrix is sixteen bytes, needs no texture, and
 * has the one property that matters here — the sixteen values in any small neighbourhood are
 * maximally spread over `[0,1)`, so neighbouring rays sample the volume at maximally different
 * depths and their errors cancel when the reprojection averages them. Rotating it by the frame
 * index is what turns "sixteen samples of spatial detail" into "sixteen frames of temporal
 * detail".
 */
const BAYER_4X4 = [
  0, 8, 2, 10,
  12, 4, 14, 6,
  3, 11, 1, 9,
  15, 7, 13, 5,
];

export interface VolumetricCloudsOptions {
  settings: QualitySettings;
  /** Generated synchronously instead of in a worker. Tests and headless use only. */
  noise?: ReturnType<typeof generateCloudNoise>;
  /** Injected for tests. */
  createWorker?: () => Worker;
  /**
   * Whether the renderer's other buffers are mirrored in Y relative to `screenUV`.
   *
   * **True on both backends, which is the default. Do not derive this from the backend** — that is
   * precisely the mistake that cost WebGL2 its entire cloud layer for two milestones, and
   * {@link VolumetricClouds.screenFlipY} quotes the measurement. The option exists so the wrong
   * value can be *forced* while diagnosing, and so a future backend that genuinely disagrees has
   * somewhere to say so, with numbers.
   */
  flipScreenSpaceY?: boolean;
}

export class VolumetricClouds {
  /**
   * The ground shadow: this same field, projected sun-aligned onto whatever is below it.
   *
   * Owned here rather than by the terrain because every input it needs — the volume, the coverage,
   * the layer geometry, the advection offset, the sun — is already owned here, and a second copy
   * of any of them is a shadow that drifts away from its cloud. Consumers only ever ask it for a
   * node. See `cloudShadow.ts`.
   */
  readonly shadow: CloudShadow;

  /** The 3D fields. Uploaded once; never touched again. */
  private readonly shapeTexture: Data3DTexture;
  private readonly detailTexture: Data3DTexture;
  private readonly bayerTexture: Data3DTexture;

  // --- weather ------------------------------------------------------------------------------
  private readonly coverage = uniform(0.4);
  private readonly baseAltitude = uniform(1_220);
  private readonly layerThickness = uniform(900);
  private readonly convection = uniform(0.8);
  /** Advection offset [m], so the layer drifts downwind. */
  private readonly windOffset = uniform(new Vector3(0, 0, 0));

  // --- lighting -----------------------------------------------------------------------------
  private readonly sunDirection = uniform(new Vector3(0, 1, 0));
  private readonly sunRadiance = uniform(new Vector3(1, 1, 1));
  private readonly ambient = uniform(new Vector3(0.1, 0.12, 0.16));

  // --- march --------------------------------------------------------------------------------
  private readonly steps = uniform(48);
  private readonly jitterFrame = uniform(0);
  private readonly previousViewProjection = uniform(new Matrix4());
  /*
   * The scene camera, as explicit uniforms.
   *
   * This pass is drawn by a `QuadMesh`, and `QuadMesh.render()` renders with its *own*
   * orthographic camera. So inside this material the TSL globals `cameraPosition`,
   * `cameraWorldMatrix` and `cameraProjectionMatrixInverse` describe that quad camera — a unit
   * box at the origin — and not the aeroplane's. Using them builds every view ray from the
   * wrong camera, which does not error, does not warn, and produces a cloud layer that is
   * simply never where you are looking. Three's own `TRAANode` takes the scene camera as a
   * constructor argument for exactly this reason.
   */
  private readonly viewerPosition = uniform(new Vector3());
  private readonly inverseProjection = uniform(new Matrix4());
  private readonly cameraToWorld = uniform(new Matrix4());
  private readonly historyValid = uniform(0);
  /**
   * How much of the previous frame the blend keeps, 0 disabling reprojection entirely.
   *
   * **On, from M7 part 1, on every tier.** It was off for a milestone because the blend left
   * hard-edged bands of differently-blended cloud across the sky, and the two defects behind that
   * are both fixed and both pinned by `cloudReprojection.test.ts` — see the long note at the
   * reprojection itself. Briefly: the reprojected coordinate was flipped in Y against a buffer that
   * is already stored mirrored, so a *still* camera read the wrong row and fed it back at 0.9; and
   * the point it reprojected through was the middle of the shell interval, which jumps at a terrain
   * silhouette and goes negative where the ray misses the shell, so the blend switched off along a
   * hard edge wherever it did.
   *
   * What it buys is the cheap route to more samples: ten frames of rotated dither average to a
   * clean image, so the effective sample count is ten times the real one. That is what would let
   * the step count come down on weak hardware without banding, which matters most at Low.
   */
  private readonly temporalBlend = uniform(0);
  /** 1 while volumetric clouds are on; 0 collapses the pass to fully transparent. */
  private readonly enabled = uniform(1);
  /**
   * 1 when everything *outside* this pass must be addressed with Y mirrored relative to `screenUV`.
   *
   * This pass casts its rays from `screenUV`, so `screenUV` is the space its buffer is laid out in.
   * On the WebGPU backend that space is a vertical mirror of the one the renderer's other buffers
   * use, and the mismatch bites in exactly two places — both of them silent:
   *
   *   - **Reading the scene depth.** Sampled unmirrored, every ray gets the depth of the pixel
   *     reflected about the middle of the screen. Upward rays, which see sky, read the *ground's*
   *     depth of a few metres, so `end` clamps below `start` and the march is skipped. On every
   *     upward ray, on every frame, with every intermediate value in the shader correct and no
   *     error anywhere. This is what cost milestone 3 three attempts.
   *   - **Compositing the result.** Unmirrored, the finished layer arrives upside down: cloud along
   *     the bottom of the frame and clear sky above it.
   *
   * **It is 1 on both backends.** It was 0 on WebGL2 for two milestones, on the reasoning that
   * WebGL's bottom-left framebuffer origin cancels the mirror — which is the sort of argument that
   * is exactly as convincing whether or not it is true. It is not true, and the cost was the whole
   * cloud layer on that backend, silently. `probeInternals` on WebGL2, at KSFO with terrain in the
   * depth buffer:
   *
   * ```
   *   steps          min 0  max 0   mean 0.00   ← the shipped coordinate, screenFlipY = 0
   *   stepsMirrored  min 0  max 48  mean 11.24  ← its mirror
   *   intervalKm     min 1.36  max 42.06        ← the geometry was never the problem
   *   density        max 0.75   shapeRed max 0.84
   *   enabled 1   noiseReady 1
   * ```
   *
   * Which is, to the digit, the same signature the WebGPU backend produced before this uniform
   * existed. Two backends, one convention, one value.
   *
   * A uniform rather than a constant because it is the knob that makes this measurable at all, and
   * because a third backend may yet disagree. But it is no longer set *from* the backend: nothing
   * derives it, and anything that wants to change it has to justify itself against the numbers
   * above. The obvious alternative spelling (`uv()`) is *not* the mirror of `screenUV` here —
   * swapping it in put the step count straight back to zero on WebGPU, which is the second time a
   * plausible-looking coordinate has cost this pass a milestone.
   */
  private readonly screenFlipY = uniform(1);
  /**
   * 0 until the worker delivers the noise volumes.
   *
   * Separate from {@link enabled} on purpose: "the tier does not want clouds" and "the field is
   * not built yet" are different states, and collapsing them would mean a tier change during
   * start-up could switch clouds on before there is anything to march through — which does not
   * render nothing, it renders a uniform grey haze from the all-zero volume.
   */
  private readonly noiseReady = uniform(0);

  /** Near/far of the scene camera, for turning its depth buffer back into view-space Z. */
  private readonly cameraNear = uniform(0.1);
  private readonly cameraFar = uniform(80_000);

  // --- the render resources this pass owns outright ------------------------------------------
  /** Written every frame; this is the texture the post chain composites. */
  private readonly cloudTarget: RenderTarget;
  /** Last frame's result, rolled forward by a blit so its identity never changes. */
  private readonly historyTarget: RenderTarget;
  private readonly material: NodeMaterial;
  /** Rolls the frame just drawn into the history target. See {@link render}. */
  private readonly copyMaterial: NodeMaterial;
  /** Both built on first use by {@link probeInternals}; never part of a shipped frame. */
  private geometryMaterial: NodeMaterial | null = null;
  private depthMaterial: NodeMaterial | null = null;
  /** The composite input. A plain texture node — a value, with no lifecycle to lose. */
  private readonly compositeTexture: Node<'vec4'>;

  private settings: QualitySettings;
  private driftX = 0;
  private driftZ = 0;
  private hasHistory = false;
  private frameIndex = 0;
  /** Null until the post chain publishes the scene depth the occlusion test reads. */
  private sceneDepth: DepthTexture | null = null;
  private cloudinessValue = 0;
  /**
   * The shape volume's bytes, kept for CPU-side sampling.
   *
   * Not a duplicate of the texture: `Data3DTexture` hands its data to the GPU and there is no
   * defined way to read it back, so the in-cloud test needs its own reference to the same array.
   * It costs nothing — it *is* the same array.
   */
  private shapeData: Uint8Array | null = null;
  private shapeSize = SHAPE_SIZE;

  constructor(options: VolumetricCloudsOptions) {
    this.settings = options.settings;
    this.screenFlipY.value = (options.flipScreenSpaceY ?? true) ? 1 : 0;

    // Half float: the scattering term is HDR — cloud lit by a low sun is genuinely brighter than
    // 1 — and an 8-bit target would band the sky and clip the bright edges to flat white.
    this.cloudTarget = makeCloudTarget('SkySim.clouds');
    this.historyTarget = makeCloudTarget('SkySim.clouds.history');
    this.compositeTexture = texture(this.cloudTarget.texture).sample(this.toBufferUv(screenUV));

    this.material = configureQuadMaterial(new NodeMaterial(), 'SkySim.clouds');
    this.copyMaterial = configureQuadMaterial(new NodeMaterial(), 'SkySim.clouds.roll');
    this.copyMaterial.colorNode = texture(this.cloudTarget.texture).sample(uv());

    /*
     * The volumes exist immediately at their final size, empty, and are filled when the worker
     * delivers. Allocating them up front means the shader, the sampler and the pipeline are all
     * built once — swapping a texture's *contents* costs an upload, swapping its dimensions
     * would invalidate the pipeline and recompile the most expensive shader in the renderer
     * a second into the session.
     */
    const shapeSize = options.noise?.shapeSize ?? SHAPE_SIZE;
    const detailSize = options.noise?.detailSize ?? DETAIL_SIZE;
    this.shapeSize = shapeSize;
    const shapeBytes = options.noise?.shape ?? new Uint8Array(shapeSize ** 3 * 4);
    if (options.noise !== undefined) this.shapeData = shapeBytes;
    this.shapeTexture = makeVolume(shapeBytes, shapeSize, RGBAFormat);
    this.detailTexture = makeVolume(
      options.noise?.detail ?? new Uint8Array(detailSize ** 3 * 4),
      detailSize,
      RGBAFormat,
    );
    if (options.noise !== undefined) this.noiseReady.value = 1;
    else this.startWorker(options.createWorker);

    // The dither, as a 4×4×1 red volume. A tiny 3D texture rather than a constant array because
    // an indexed constant array in WGSL forces the whole thing into scratch memory in the inner
    // loop; a texture fetch outside the loop costs nothing.
    const bayer = new Uint8Array(16);
    for (let i = 0; i < 16; i++) bayer[i] = Math.round((BAYER_4X4[i] / 16) * 255);
    this.bayerTexture = makeVolume(bayer, 4, RedFormat, 4, 4, 1);

    // The ground shadow reads the same volume this pass marches — one texture, one upload, one
    // field, so the shadow is always under the cloud casting it. See `cloudShadow.ts`.
    this.shadow = new CloudShadow({ shape: this.shapeTexture });

    this.applyQuality(options.settings);
  }

  /**
   * Republish the shadow's on/off state.
   *
   * Two conditions, and they are genuinely different: the tier has to *want* cloud shadows, and
   * the noise volumes have to have arrived. Without the second, the first frames of a session
   * would project an all-zero field, whose coverage remap is not "no cloud" but a uniform
   * mid-density — a flat grey wash over the whole world.
   */
  private refreshShadowAmount(): void {
    const clouds = this.settings.clouds;
    const wanted =
      clouds.castShadows && clouds.mode === 'volumetric' && this.noiseReady.value === 1;
    this.shadow.setAmount(wanted ? 1 : 0);
  }

  /**
   * Kick off generation off the main thread.
   *
   * Failure is not fatal and must not be: a browser that refuses to start the worker still has
   * to fly. The field simply stays empty, which reads as a clear sky — a defensible outcome
   * rather than a broken one.
   */
  private startWorker(createWorker?: () => Worker): void {
    let worker: Worker;
    try {
      worker =
        createWorker?.() ??
        new Worker(new URL('./cloudNoise.worker.ts', import.meta.url), { type: 'module' });
    } catch (error) {
      console.warn('OpenSkies: the cloud-noise worker could not start; flying under a clear sky —', error);
      return;
    }

    worker.addEventListener('message', (event: MessageEvent<CloudNoiseResponse>) => {
      const { shape, detail } = event.data;
      (this.shapeTexture.image as { data: Uint8Array }).data = shape;
      (this.detailTexture.image as { data: Uint8Array }).data = detail;
      this.shapeData = shape;
      this.shapeTexture.needsUpdate = true;
      this.detailTexture.needsUpdate = true;
      this.noiseReady.value = 1;
      this.refreshShadowAmount();
      worker.terminate();
    });
    worker.addEventListener('error', (event) => {
      console.warn('OpenSkies: cloud-noise generation failed —', event.message);
      worker.terminate();
    });

    worker.postMessage({ options: { shapeSize: SHAPE_SIZE, detailSize: DETAIL_SIZE } });
  }

  /**
   * Bind the scene's depth buffer and compile the march against it.
   *
   * Called by the post chain whenever it (re)builds its targets, because the depth texture is
   * one of *its* resources and a rebuild replaces it. Compiling here rather than in the
   * constructor is what lets the shader read real scene depth: a cloud in front of a mountain has
   * to be occluded by it, and that test needs the depth of the frame the cloud is being drawn
   * into.
   */
  setSceneDepth(depthTexture: DepthTexture): void {
    if (this.sceneDepth === depthTexture) return;
    this.sceneDepth = depthTexture;
    this.material.colorNode = this.buildRaymarch(
      texture(depthTexture),
      texture(this.historyTarget.texture),
    );
    this.material.needsUpdate = true;
  }

  /**
   * The node the post chain composites: `rgb` is in-scattered radiance, `a` is the transmittance
   * of the layer, so the composite is `scene · a + rgb` — the same attenuate-then-add the aerial
   * perspective uses, because it is the same physics with a much denser medium.
   */
  get compositeNode(): Node<'vec4'> {
    return this.compositeTexture;
  }

  /**
   * A `screenUV`-space coordinate, expressed the way the rest of the renderer addresses buffers.
   *
   * **Two** reads cross out of this pass's own space and both go through here: the scene depth
   * (in the march) and the cloud buffer (at composite time). The reprojected history deliberately
   * does *not* — it is already in this pass's own space at both ends, and adding the mirror there
   * made a still camera feed back the wrong row. See the note at the reprojection, and
   * {@link screenFlipY} for what the mirror is and why it is unconditional.
   */
  private toBufferUv(coordinate: Node<'vec2'>): Node<'vec2'> {
    return vec2(
      coordinate.x,
      coordinate.y.mul(float(1).sub(this.screenFlipY.mul(2))).add(this.screenFlipY),
    );
  }

  /** True once there is a compiled march to run. */
  get isReady(): boolean {
    return this.sceneDepth !== null;
  }

  /**
   * Which way up this pass addresses the renderer's other buffers: 1 mirrors, 0 does not.
   *
   * Exposed because it is the single value that decides whether this layer renders at all, and
   * because the only other way to see it is a GPU readback through {@link probeInternals}. See
   * {@link screenFlipY} for what it is and what happened when it was wrong.
   */
  get screenSpaceFlipY(): number {
    return this.screenFlipY.value as number;
  }

  /**
   * How much cloud the aircraft is currently sitting in, 0…1.
   *
   * Drives the in-cloud whiteout and the light buffet. Sampled on the CPU from the same volumes
   * the shader marches — see {@link updateFrame}.
   */
  get cloudiness(): number {
    return this.cloudinessValue;
  }

  /**
   * Read the cloud buffer back off the GPU and summarise it.
   *
   * Kept in the shipped build for the same reason the `__SKYSIM__` handle is. When the layer looks
   * wrong there are exactly two questions — did the pass run, and what did it produce — and
   * sampling the buffer through the render chain cannot answer either: a buffer nobody wrote and a
   * buffer full of "clear air" composite identically. This answers both with numbers, and it is how
   * the coordinate-precision bug in {@link sampleDensity} was finally pinned down.
   *
   * Slow (it stalls the pipeline). Diagnostics only, never per frame.
   *
   * **The readback is `Uint16Array`, not floats.** The buffer is half-float, and three hands back
   * its raw bits; a transmittance of 1.0 arrives as 15360 (`0x3C00`). Reading those as numbers
   * gives a table that is monotonic in the right direction and wrong by four orders of magnitude,
   * which is the most expensive kind of wrong — it looks like data. {@link decodeHalfFloat} is what
   * makes this a measurement rather than a bit pattern.
   */
  async probe(renderer: Renderer): Promise<{
    width: number;
    height: number;
    frames: number;
    alphaMin: number;
    alphaMax: number;
    alphaMean: number;
    scatterMax: number;
  }> {
    const width = this.cloudTarget.width;
    const height = this.cloudTarget.height;
    const pixels = decodeHalfFloat(
      (await renderer.readRenderTargetPixelsAsync(
        this.cloudTarget,
        0,
        0,
        width,
        height,
      )) as ArrayLike<number>,
    );

    let alphaMin = Infinity;
    let alphaMax = -Infinity;
    let alphaSum = 0;
    let scatterMax = 0;
    let count = 0;
    for (let i = 0; i < pixels.length; i += 4) {
      const a = pixels[i + 3];
      if (a < alphaMin) alphaMin = a;
      if (a > alphaMax) alphaMax = a;
      alphaSum += a;
      scatterMax = Math.max(scatterMax, pixels[i], pixels[i + 1], pixels[i + 2]);
      count++;
    }
    return {
      width,
      height,
      frames: this.frameIndex,
      alphaMin,
      alphaMax,
      alphaMean: alphaSum / Math.max(1, count),
      scatterMax,
    };
  }

  /**
   * Draw the layer.
   *
   * Called by hand, once per frame, from the render step — after the scene (whose depth this
   * reads) and before the post chain composites. `updateFrame(alpha)`: no; this is the render
   * half, kept separate so the frame loop's ordering is visible at the call site rather than
   * buried in a node's lifecycle.
   *
   * Allocation-free: the size vector, the matrix and the renderer-state object are all module
   * scratch.
   */
  render(renderer: Renderer, camera: PerspectiveCamera): void {
    if (this.sceneDepth === null) return;
    if (this.settings.clouds.mode !== 'volumetric') return;

    renderer.getDrawingBufferSize(scratchSize);
    const scale = this.settings.clouds.resolutionScale;
    const width = Math.max(1, Math.floor(scratchSize.x * scale));
    const height = Math.max(1, Math.floor(scratchSize.y * scale));

    rendererState = RendererUtils.resetRendererState(renderer, rendererState);

    if (this.cloudTarget.width !== width || this.cloudTarget.height !== height) {
      this.cloudTarget.setSize(width, height);
      this.historyTarget.setSize(width, height);
      renderer.initRenderTarget(this.cloudTarget);
      renderer.initRenderTarget(this.historyTarget);
      // The history is now the wrong resolution and, worse, the wrong *aspect*, so reprojecting
      // from it would smear the whole layer sideways for a frame.
      this.hasHistory = false;
    }

    camera.updateMatrixWorld();
    this.viewerPosition.value.setFromMatrixPosition(camera.matrixWorld);
    (this.inverseProjection.value as Matrix4).copy(camera.projectionMatrixInverse);
    (this.cameraToWorld.value as Matrix4).copy(camera.matrixWorld);
    this.cameraNear.value = camera.near;
    this.cameraFar.value = camera.far;
    this.historyValid.value = this.hasHistory ? 1 : 0;
    // Rotates the dither. All that matters is that it advances, so consecutive frames sample the
    // volume at different depths and the reprojection averages them.
    this.jitterFrame.value = this.frameIndex++ % 16;

    quad.material = this.material;
    renderer.setRenderTarget(this.cloudTarget);
    quad.render(renderer);

    /*
     * Roll the history forward by *drawing* into it rather than blitting into it.
     *
     * `copyTextureToTexture` is the obvious way and it does not work here. The history target is
     * never something the renderer draws into — it is only a copy destination and a sampler input
     * — and three's WebGPU backend allocates a texture's GPU resource on first use. First use is
     * the sampler, built when the march is compiled, when the target is still its construction
     * size of 1×1; resizing the *render target* afterwards does not move a GPU texture that was
     * created down the sampler path, and neither `initRenderTarget` nor binding-and-clearing it
     * rehomes it. The blit then fails validation with "copy range touches outside of texture mip
     * level 0 size (1×1)" every frame, forever — loudly, at least, unlike the last bug.
     *
     * A one-line fullscreen copy pass makes the history a target the renderer genuinely renders
     * into, which allocates it at the right size through the ordinary path. It costs a texture
     * fetch and a write per half-resolution pixel, which is what the blit cost anyway, and both
     * texture handles stay stable for the lifetime of the object.
     */
    renderer.setRenderTarget(this.historyTarget);
    quad.material = this.copyMaterial;
    quad.render(renderer);
    renderer.setRenderTarget(null);

    // Record the view-projection that produced the history — reprojection is exactly "where was
    // this world point on screen last frame".
    scratchMatrix.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    (this.previousViewProjection.value as Matrix4).copy(scratchMatrix);
    this.hasHistory = true;

    RendererUtils.restoreRendererState(renderer, rendererState);
  }

  /**
   * This pixel's view ray, and where it crosses the cloud shell.
   *
   * Shared by the march and by {@link buildDiagnostic} so the probe measures the *same* geometry
   * the shipped path uses rather than a second implementation of it — which would be a second
   * thing to be wrong. Everything here is checked against
   * {@link import('./shellInterval.js').shellInterval}, an f32-faithful CPU port with its own
   * tests; if the two ever disagree, the port is the one that has been proved.
   *
   * The interval is returned *before* the scene-depth clamp, because the diagnostic wants to know
   * what the geometry said and the march wants to know what survived the terrain.
   */
  private buildRayInterval() {
    /*
     * `screenUV`, not the quad's own `uv()` — and they are the **same coordinate**.
     *
     * An earlier version of this comment said they "differ by a vertical flip on the WebGPU
     * backend". That is false, and the false version is what produced a backend-conditional
     * {@link screenFlipY} and cost the WebGL2 path its entire cloud layer. Read from three's own
     * source rather than assumed this time:
     *
     *   - `QuadGeometry` has positions `[-1,3, -1,-1, 3,-1]` against uvs `[0,-1, 0,1, 2,1]`, so on
     *     the quad `uv.y = (1 − ndc.y) / 2` — top-down.
     *   - `ScreenNode.generate` rewrites the fragment coordinate as `size.y − coord.y` when
     *     `builder.isFlipY()`, which is `true` for GLSL and `false` for WGSL, explicitly to "follow
     *     webgpu standards". So `screenUV.y = 0` is the *top* of the buffer on both backends.
     *
     * Both are top-down, on both backends. `screenUV` is preferred only because it is what three's
     * own passes reconstruct rays from and it does not depend on the quad's attributes; swapping in
     * `uv()` is a no-op, and the *reason* the historical attempt to do so measured zero steps was
     * that it changed nothing while something else was still wrong.
     */
    const screen = screenUV.toVar();

    /*
     * --- reconstruct the view ray ------------------------------------------------------------
     *
     * `ndc.y = 2·screen.y − 1` with no negation, while `screen.y` is top-down. That is deliberate
     * and it is the origin of the mirror this whole file keeps talking about: the ray stored at
     * buffer row `v` is the ray for screen position `1 − v`, so **this pass's buffer is stored
     * vertically mirrored** — on both backends — and every read that crosses out of it has to be
     * mirrored back. That is exactly what {@link toBufferUv} does.
     *
     * three's own `getViewPosition` applies `screenPosition.y.oneMinus()` in *both* of its
     * coordinate-system branches, so the un-mirrored spelling here would be `1 − 2·screen.y`.
     * **Do not "fix" it to that in isolation.** It is not a local change: un-mirroring the buffer
     * requires flipping `screenFlipY` to 0 for the depth fetch *and* the composite, *and* adding a
     * mirror to the reprojection, which currently and correctly has none. Four coordinated edits,
     * `cloudReprojection.test.ts` to re-derive, and no behavioural gain whatsoever.
     */
    const ndc = screen.mul(2).sub(1);
    const clip = vec4(ndc.x, ndc.y, float(1), float(1));
    const viewRay = this.inverseProjection.mul(clip);
    // A point on this pixel's ray, in view space. Kept unnormalised because its `z` is the
    // conversion factor between camera-axis depth and distance along the ray.
    const rayView = viewRay.xyz.div(viewRay.w).toVar();
    const rayDirection = normalize(this.cameraToWorld.mul(vec4(rayView, 0)).xyz).toVar();

    // --- intersect the shell ------------------------------------------------------------------
    // The planet's centre sits one Earth radius below the tangent-plane origin.
    const centre = vec3(0, -EARTH_RADIUS_M, 0);
    const origin = vec3(this.viewerPosition).sub(centre).toVar();
    const innerRadius = float(EARTH_RADIUS_M).add(this.baseAltitude);
    const outerRadius = innerRadius.add(this.layerThickness);

    const b = dot(origin, rayDirection).toVar();
    /*
     * The viewer's radius, and the shell offsets as *small* numbers.
     *
     * The textbook form of this intersection is `t = −b ± √(b² − (c₀ − r²))` with
     * `c₀ = |origin|²`, and in FP32 it is a trap. `c₀` and `r²` are both ≈ 4.06 × 10¹³, where
     * single precision quantises to steps of 4.2 million; their true difference is ≈ 1.2 × 10¹⁰,
     * and then `−b + √(…)` subtracts two numbers that agree to six significant figures. Each of
     * those steps is individually survivable and together they are how a cloud layer ends up
     * either 900 m away or 6000 km away depending on rounding.
     *
     * So neither square is ever formed. `r² − rᵥ²` factors exactly as `(rᵥ − r)(rᵥ + r)`, whose
     * first factor is the metres of altitude between the eye and the shell — a small, precise
     * number — and whose second is a benign ~1.27 × 10⁷.
     */
    const viewerRadius = origin.length().toVar();
    const kInner = viewerRadius.sub(innerRadius).mul(viewerRadius.add(innerRadius)).toVar();
    const kOuter = viewerRadius.sub(outerRadius).mul(viewerRadius.add(outerRadius)).toVar();

    const below = viewerRadius.lessThan(innerRadius);
    const inside = viewerRadius
      .greaterThanEqual(innerRadius)
      .and(viewerRadius.lessThan(outerRadius));

    const innerHit = shellRoots(b, kInner);
    const outerHit = shellRoots(b, kOuter);

    /*
     * Where the march starts and ends.
     *
     * Three cases, and they are all reachable in normal flight, which is why none can be
     * skipped: below the layer looking up (start at the inner shell, end at the outer),
     * inside it (start here, end where the ray leaves through the outer shell), and above it
     * looking down.
     *
     * Inside the layer the march is allowed to run to the outer shell even on a downward ray
     * that leaves through the inner one. It costs a few steps below the cloud base, and they
     * contribute nothing: the height fraction clamps to 0 there and the vertical gradient's
     * `smoothstep(0, 0.11, 0)` is exactly 0, so the density is too.
     *
     * The third case is the one with teeth, and it has two sub-cases the obvious formulation
     * runs together. "Enter at the top, leave at the base" is only right when the ray *reaches*
     * the base: a shallow downward ray from cruise dips into the layer and climbs back out
     * without touching it — 2° down from 6000 m bottoms out at 2115 m under a 4000 m top — and
     * for that ray `innerHit.near` is the closest-approach distance, which is the middle of the
     * chord. The march then stopped at the bottom of the arc and the far half of the deck ended
     * along a curved line. When the base is missed the ray leaves through the top instead.
     *
     * And when the *outer* shell is missed there is no layer along this ray at all, in any
     * region, because that shell is the boundary of the whole thing. `−1` fails the interval
     * test against a `start` already clamped to zero, which is cheaper than a second comparison
     * in the hottest shader in the renderer.
     */
    const start = below.select(innerHit.far, inside.select(float(0), outerHit.near)).toVar();
    const aboveEnd = outerHit.hit.select(
      innerHit.hit.select(innerHit.near, outerHit.far),
      float(-1),
    );
    const end = below.select(outerHit.far, inside.select(outerHit.far, aboveEnd)).toVar();
    start.assign(max(start, float(0)));

    return { screen, rayView, rayDirection, origin, innerRadius, viewerRadius, start, end };
  }

  /**
   * A material that writes the shell **geometry** instead of the march's result.
   *
   * The readback probe can only see what the shader wrote, and "an interval the march skipped"
   * and "an interval it marched through empty density" produce the same output — a buffer of
   * transmittance 1.0 — which is exactly the ambiguity that cost this pass two attempts. This
   * resolves it by writing four numbers that cannot be inferred from the outside:
   *
   *   r — the interval length `end − start`, in kilometres, **before** the scene-depth clamp. Zero
   *       means the geometry rejected the ray on its own; non-zero means the geometry is fine and
   *       the answer is downstream, in the depth clamp or the density.
   *   g — the density at the middle of the interval, from the same {@link sampleDensity} the
   *       march uses. Compare against {@link densityAt}, which is the CPU port of it.
   *   b — the *raw* red channel of the shape volume at that point. This is the one that separates
   *       "the density function rejects everything" from "the 3D texture fetch returns nothing",
   *       and there is no other way to tell those apart from outside the GPU.
   *   a — the viewer's height above the cloud base, in metres.
   *
   * A separate material rather than a branch in the shipped one: a `select` would evaluate both
   * sides in the hottest shader in the renderer, and a uniform branch is exactly the kind of
   * construct that has already silently mis-lowered here once.
   *
   * Paired with {@link buildDepthDiagnostic}, which covers the *other* half — everything the march
   * has that the geometry does not. {@link probeInternals} renders both, because a single four-channel
   * target cannot carry both and choosing between them is how a diagnosis goes down a blind alley:
   * this pass's whole history is of one half looking healthy while the other was broken.
   */
  private buildGeometryDiagnostic(): Node<'vec4'> {
    return Fn(() => {
      const ray = this.buildRayInterval();
      const sampleShape = (at: Node<'vec3'>) => texture3D(this.shapeTexture, at, 0);
      const sampleDetail = (at: Node<'vec3'>) => texture3D(this.detailTexture, at, 0);

      const lengthM = ray.end.sub(ray.start).toVar();
      // The middle of whatever interval the geometry produced. Clamped to a non-negative distance
      // so a rejected ray samples *somewhere* defined rather than behind the eye — the density and
      // the raw fetch then still say whether the volume is readable at all, which is the question
      // this channel exists to answer independently of the geometry.
      const mid = max(ray.start.add(lengthM.mul(0.5)), float(0)).toVar();
      const position = ray.origin.add(ray.rayDirection.mul(mid)).toVar();
      const height = position.length().sub(ray.innerRadius).div(this.layerThickness).toVar();

      return vec4(
        lengthM.div(1_000),
        this.sampleDensity(sampleShape, sampleDetail, position, height),
        /*
         * The raw fetch, at the coordinate the march would use.
         *
         * Written out here rather than shared with {@link sampleDensity} through a helper: that
         * function is the hottest shader in the renderer and a helper would recompute the scaled
         * position for it, so the *numbers* are shared through {@link SHAPE_SAMPLE_SCALE} and
         * {@link SHAPE_VERTICAL_SPAN} instead and the expression is written twice. If either
         * constant moves, both move together, which is the property that matters.
         */
        sampleShape(
          vec3(
            position.add(vec3(this.windOffset)).mul(SHAPE_SAMPLE_SCALE).x,
            clamp(height, 0, 1).mul(SHAPE_VERTICAL_SPAN),
            position.add(vec3(this.windOffset)).mul(SHAPE_SAMPLE_SCALE).z,
          ),
        ).r,
        ray.viewerRadius.sub(ray.innerRadius),
      );
    })();
  }

  /**
   * The other half: the scene-depth clamp and the step count it produces.
   *
   * ## Why the A/B is written the way it is
   *
   * The view ray is reconstructed from `screenUV`, and if the depth texture does not agree with
   * that convention then every upward ray reads the *ground's* depth of a few metres and is clamped
   * to nothing. So both ways up are evaluated in one shader invocation and the step count is
   * reported for each.
   *
   * It compares the shipped coordinate against **its own mirror**, computed here, and *not* against
   * the raw `screenUV`. That is a fix, not a stylistic choice. Written as
   * `toBufferUv(screen)` versus `screen`, the A/B is degenerate on any backend where
   * {@link screenFlipY} is 0 — the two expressions are then literally the same one, both columns
   * report identical numbers, and the probe answers a question it was never asked. That is exactly
   * what happened on the WebGL2 backend: it reported 0 steps for both conventions and identical
   * scene distances, which reads as "the flip is not the problem" when in fact the instrument was
   * blind. Mirroring explicitly keeps the two columns genuinely different on both backends.
   *
   * Note also that the min/max aggregation in {@link probeInternals} cannot detect a flip from the
   * distance channels alone: flipping an image does not change the *set* of values in it, so the
   * two distance columns agree to the last digit whether the flip is right or wrong. **The step
   * count is the channel that carries the answer**, because it combines the depth with the ray.
   */
  private buildDepthDiagnostic(): Node<'vec4'> {
    return Fn(() => {
      const ray = this.buildRayInterval();

      /*
       * The step counter is the measurement that matters, and it is the one no readback of the
       * shipped buffer can produce. "The march skipped the ray" and "the march ran and found
       * nothing" both write transmittance 1.0 — which is *also* the renderer's clear alpha, so
       * they are additionally indistinguishable from "the pass never wrote at all". Three states,
       * one observation. Counting the iterations of the inner `If` separates them.
       */
      const span = min(ray.end.sub(ray.start), float(56_000));
      const stepLength = span.div(this.steps).toVar();
      const dither = texture3D(
        this.bayerTexture,
        vec3(ray.screen.mul(vec2(0.25, 0.25)), float(0)),
        0,
      ).r;
      const jitter = dither.add(this.jitterFrame.mul(1 / 16)).fract().toVar();

      /*
       * The scene-depth clamp, sampled **both ways up**.
       *
       * This is the last input the march has that the geometry above does not, and the A/B is the
       * whole point: the view ray is reconstructed from `screenUV`, and if the depth texture does
       * not agree with that convention then every upward ray reads the *ground's* depth and is
       * clamped to a few metres, while every downward ray reads the sky's. The march is then
       * skipped on the entire screen — but only once there is terrain in the depth buffer, which
       * is why an empty one looks healthy.
       */
      const depthTexture = texture(this.sceneDepth as DepthTexture);

      const distanceFor = (uvw: Node<'vec2'>): Node<'float'> => {
        const viewZ = perspectiveDepthToViewZ(
          depthTexture.sample(uvw).r,
          this.cameraNear,
          this.cameraFar,
        );
        return ray.rayView.mul(viewZ.div(min(ray.rayView.z, float(-1e-4)))).length();
      };

      const stepsFor = (sceneDistance: Node<'float'>): Node<'float'> => {
        const end = min(ray.end, sceneDistance).toVar();
        const distance = ray.start.add(jitter.mul(stepLength)).toVar();
        const taken = float(0).toVar();
        Loop(MAX_RAYMARCH_STEPS, () => {
          If(distance.lessThan(end), () => {
            taken.addAssign(1);
            distance.addAssign(stepLength);
          });
        });
        return taken;
      };

      const shippedUv = this.toBufferUv(ray.screen).toVar();
      // The mirror of whatever the shipped convention is, not of `screenUV`. See the header — the
      // previous spelling collapsed to a comparison of one expression against itself wherever
      // `screenFlipY` is 0, which is the entire WebGL2 backend.
      const mirroredUv = vec2(shippedUv.x, float(1).sub(shippedUv.y)).toVar();
      const shipped = distanceFor(shippedUv).toVar();
      const mirrored = distanceFor(mirroredUv).toVar();
      return vec4(
        shipped.div(1_000),
        mirrored.div(1_000),
        stepsFor(shipped),
        stepsFor(mirrored),
      );
    })();
  }

  /**
   * Read the march's intermediates back off the GPU. Diagnostics only; see {@link buildDiagnostic}.
   *
   * Renders one extra full-screen pass into the cloud buffer, so the next real frame overwrites it
   * and nothing is left behind.
   */
  async probeInternals(
    renderer: Renderer,
    camera: PerspectiveCamera,
  ): Promise<Record<string, number>> {
    if (this.geometryMaterial === null) {
      this.geometryMaterial = configureQuadMaterial(new NodeMaterial(), 'SkySim.clouds.debug.geom');
      this.geometryMaterial.colorNode = this.buildGeometryDiagnostic();
    }
    if (this.depthMaterial === null) {
      this.depthMaterial = configureQuadMaterial(new NodeMaterial(), 'SkySim.clouds.debug.depth');
      this.depthMaterial.colorNode = this.buildDepthDiagnostic();
    }
    camera.updateMatrixWorld();
    this.viewerPosition.value.setFromMatrixPosition(camera.matrixWorld);
    (this.inverseProjection.value as Matrix4).copy(camera.projectionMatrixInverse);
    (this.cameraToWorld.value as Matrix4).copy(camera.matrixWorld);
    this.cameraNear.value = camera.near;
    this.cameraFar.value = camera.far;

    /*
     * Both halves, in one probe.
     *
     * Never one or the other. Every previous round of this bug had one half measuring perfectly
     * healthy while the other was broken — a valid interval and a density of 0.88 in the same frame
     * the shipped buffer was empty — so a probe that reports only the half you thought to ask about
     * is how three attempts were spent. The two are read back separately because four channels
     * cannot carry eight numbers.
     */
    const geometry = await this.renderProbe(renderer, this.geometryMaterial, [
      'intervalKm',
      'density',
      'shapeRed',
      'heightAboveBaseM',
    ]);
    const depth = await this.renderProbe(renderer, this.depthMaterial, [
      'sceneDistKm',
      'sceneDistKmMirrored',
      'steps',
      'stepsMirrored',
    ]);

    return {
      ...geometry,
      ...depth,
      // Not derivable from the pixels, and both are gates the step count deliberately ignores — so
      // a reader can tell "the geometry rejected the ray" from "the march was switched off".
      enabled: this.enabled.value as number,
      noiseReady: this.noiseReady.value as number,
      screenFlipY: this.screenFlipY.value as number,
    };
  }

  /**
   * Render one diagnostic material into the cloud buffer and summarise its four channels.
   *
   * Renders over the cloud buffer, so the next real frame overwrites it and nothing is left behind.
   */
  private async renderProbe(
    renderer: Renderer,
    material: NodeMaterial,
    names: readonly [string, string, string, string],
  ): Promise<Record<string, number>> {
    rendererState = RendererUtils.resetRendererState(renderer, rendererState);
    quad.material = material;
    renderer.setRenderTarget(this.cloudTarget);
    quad.render(renderer);
    renderer.setRenderTarget(null);
    RendererUtils.restoreRendererState(renderer, rendererState);

    const width = this.cloudTarget.width;
    const height = this.cloudTarget.height;
    const pixels = decodeHalfFloat(
      (await renderer.readRenderTargetPixelsAsync(this.cloudTarget, 0, 0, width, height)) as
        ArrayLike<number>,
    );

    const stats: Record<string, number> = { pixels: pixels.length / 4 };
    for (let c = 0; c < 4; c++) {
      let lo = Infinity;
      let hi = -Infinity;
      let sum = 0;
      let nan = 0;
      let count = 0;
      for (let i = c; i < pixels.length; i += 4) {
        const value = pixels[i];
        if (Number.isNaN(value)) nan++;
        else {
          if (value < lo) lo = value;
          if (value > hi) hi = value;
          sum += value;
          count++;
        }
      }
      stats[`${names[c]}Min`] = lo;
      stats[`${names[c]}Max`] = hi;
      // The mean as well as the range, because a range says "some pixel somewhere" and a mean says
      // how much of the screen. A density that peaks at 0.9 on four pixels and a density that
      // averages 0.4 are different diagnoses and the range alone cannot tell them apart.
      stats[`${names[c]}Mean`] = count === 0 ? Number.NaN : sum / count;
      if (nan > 0) stats[`${names[c]}NaN`] = nan;
    }
    return stats;
  }

  /**
   * The raymarch itself.
   *
   * Written as one TSL function rather than split up, because every intermediate is consumed
   * immediately by the next line and splitting it would mean threading a dozen values through
   * function boundaries for no gain in clarity.
   */
  private buildRaymarch(
    sceneDepth: ReturnType<typeof texture>,
    history: ReturnType<typeof texture>,
  ) {
    /*
     * Explicit level-of-detail on every volume fetch, and it is not an optimisation.
     *
     * WGSL only permits `textureSample` — which derives its mip level from screen-space
     * derivatives — in *uniform* control flow. Every one of these fetches happens inside an `If`
     * and a `Loop` whose trip count varies per pixel, which is about as non-uniform as control
     * flow gets. The result is a shader that fails validation, and a validation failure in a
     * post pass does not throw anywhere the application can see it: the pipeline is simply never
     * created and the target keeps its clear value, which for this pass is indistinguishable
     * from a perfectly clear sky. Asking for level 0 explicitly removes the derivative and makes
     * the fetch legal anywhere. The volumes have no mipmaps, so level 0 is also the only correct
     * level.
     */
    const sampleShape = (at: Node<'vec3'>) => texture3D(this.shapeTexture, at, 0);
    const sampleDetail = (at: Node<'vec3'>) => texture3D(this.detailTexture, at, 0);

    const raymarch = Fn(() => {
      const ray = this.buildRayInterval();
      const { screen, rayView, rayDirection, origin, innerRadius, start, end } = ray;

      /*
       * Never march behind the eye, and never past the terrain: a mountain in front of a cloud
       * occludes it, and without this the layer paints straight over the Alps.
       *
       * The pass gives camera-axis depth (view-space `z`), which is *not* distance along the ray
       * — at the corner of a 60° field of view they differ by 15 %, so using it directly would
       * let cloud bleed through terrain at the edges of the screen while occluding correctly in
       * the middle. Scaling the ray until its `z` matches converts one to the other exactly.
       */
      /*
       * Scene depth, sampled from the buffer the scene was just drawn into and converted back to
       * view-space Z. This is the whole reason the pass renders after the scene rather than before
       * it: without a same-frame depth the layer paints straight over the Alps.
       *
       * **`uv()` here, `screenUV` for the ray, and the difference is the whole milestone.**
       *
       * They are a vertical flip apart on the WebGPU backend, and the note above about `screenUV`
       * got half of this right: the ray genuinely must come from `screenUV`, but the claim that
       * "the depth fetch still lands on plausible values because the depth buffer is flipped the
       * same way" is false, and it is what hid this for two attempts. Sampled with `screenUV`,
       * every ray reads the depth of the pixel mirrored about the horizon — so upward rays, which
       * see sky, read the *ground's* depth of a few metres, and `end` clamps below `start`. Every
       * upward ray. The march is skipped on the entire screen, silently, with no error and with
       * every intermediate value in the shader perfectly correct.
       *
       * It is also invisible to any probe that does not have terrain in the depth buffer: with an
       * empty one every fetch returns 1.0, the flip does not matter, and the numbers look healthy.
       *
       * Measured, in one shader invocation with both coordinates evaluated side by side
       * (`probeInternals`, which still does this), on **both** backends and years apart:
       * the unmirrored coordinate takes **0** march steps on every pixel, and the mirror takes up
       * to 48–64, hitting exactly 0 where terrain occludes.
       *
       * The correction is an explicit uniform and not the quad's `uv()`, because `uv()` is not the
       * mirror of `screenUV` — it *is* `screenUV` (see the note in `buildRayInterval`), so swapping
       * it in changes nothing and reads like a fix. It is written as what it actually is: one
       * subtraction, applied unconditionally. It is **not** derived from the backend; believing it
       * was backend-specific is what cost WebGL2 its cloud layer, and {@link screenFlipY} carries
       * the numbers.
       */
      const viewZ = perspectiveDepthToViewZ(
        sceneDepth.sample(this.toBufferUv(screen)).r,
        this.cameraNear,
        this.cameraFar,
      );
      const sceneDistance = rayView.mul(viewZ.div(min(rayView.z, float(-1e-4)))).length().toVar();
      end.assign(min(end, sceneDistance));

      const scattering = vec3(0).toVar();
      const transmittance = float(1).toVar();

      If(
        end
          .greaterThan(start)
          .and(this.enabled.greaterThan(0.5))
          .and(this.noiseReady.greaterThan(0.5)),
        () => {
        /*
         * Step length.
         *
         * Capped so a ray that grazes the shell near the horizon — where the chord through the
         * layer can be a hundred kilometres — does not take hundred-metre steps and miss every
         * cloud. Beyond the cap the far end of the ray is simply not marched, which is correct:
         * at that range the layer is a solid wall of accumulated transmittance anyway.
         */
        const span = min(end.sub(start), float(56_000));
        const stepLength = span.div(this.steps).toVar();

        // Blue-noise offset, rotated by frame index. Without this the step size is visible as
        // rings; with it, it is noise the reprojection averages away.
        const dither = texture3D(
          this.bayerTexture,
          vec3(screen.mul(vec2(0.25, 0.25)), float(0)),
          0,
        ).r;
        const jitter = dither.add(this.jitterFrame.mul(1 / 16)).fract();

        const position = vec3(0).toVar();
        const distance = start.add(jitter.mul(stepLength)).toVar();

        /*
         * A compile-time trip count, with the tier's step budget in the *step length* instead.
         *
         * The obvious formulation — loop to a uniform bound — is what this originally did, and
         * it silently produced no cloud at all: a shader loop whose bound is a uniform is at the
         * mercy of how the backend chooses to lower it, and there is no error when it goes
         * wrong, just an empty buffer. A constant bound compiles to something every backend
         * agrees on. Tiers still get their quality knob, because a smaller `steps` makes each
         * step *longer*, and the ray then runs past `end` and stops early on the guard below.
         */
        Loop(MAX_RAYMARCH_STEPS, () => {
          If(transmittance.greaterThan(0.01).and(distance.lessThan(end)), () => {
            position.assign(origin.add(rayDirection.mul(distance)));

            const height = position.length().sub(innerRadius).div(this.layerThickness);
            const density = this.sampleDensity(sampleShape, sampleDetail, position, height).toVar();

            If(density.greaterThan(0.001), () => {
              // --- light march ---------------------------------------------------------------
              // Six short steps toward the sun. Enough for self-shadowing to read; a full march
              // per sample would square the cost of the whole effect for a difference nobody
              // can point at.
              const lightOpticalDepth = float(0).toVar();
              const lightStep = this.layerThickness.div(LIGHT_STEPS).mul(0.65);
              Loop(LIGHT_STEPS, ({ i }) => {
                const lightPosition = position.add(
                  vec3(this.sunDirection).mul(lightStep.mul(float(i).add(0.5))),
                );
                const lightHeight = lightPosition
                  .length()
                  .sub(innerRadius)
                  .div(this.layerThickness);
                lightOpticalDepth.addAssign(
                  this.sampleDensity(sampleShape, sampleDetail, lightPosition, lightHeight).mul(
                    lightStep,
                  ),
                );
              });

              const lightTransmittance = exp(lightOpticalDepth.mul(-0.006));

              /*
               * The powder-sugar term.
               *
               * Light entering a dense medium scatters back out close to where it went in, so a
               * cloud edge facing the sun is *darker* than the material behind it — the opposite
               * of what a surface does. Without it, backlit cloud edges glow white and the whole
               * layer reads as painted cotton wool.
               */
              const powder = float(1).sub(exp(density.mul(-14)));

              // Henyey–Greenstein, two lobes: a strong forward one for the halo when flying
              // toward the sun, and a weak backward one for the brightening opposite it.
              const cosTheta = dot(rayDirection, vec3(this.sunDirection));
              const phase = henyeyGreenstein(cosTheta, 0.72)
                .mul(0.8)
                .add(henyeyGreenstein(cosTheta, -0.28).mul(0.2));

              const stepTransmittance = exp(density.mul(stepLength).mul(-0.012));
              const sunLight = vec3(this.sunRadiance)
                .mul(lightTransmittance)
                .mul(phase)
                .mul(mix(float(1), powder, 0.7))
                .mul(12);
              const skyLight = vec3(this.ambient).mul(clamp(height.add(0.25), 0, 1));

              // Energy-conserving integration: how much this slab removed is how much it
              // scattered, so the result cannot exceed the light going in however fine the steps.
              const integrated = sunLight
                .add(skyLight)
                .mul(float(1).sub(stepTransmittance))
                .mul(transmittance);
              scattering.addAssign(integrated);
              transmittance.mulAssign(stepTransmittance);
            });

            distance.addAssign(stepLength);
          });
        });
      });

      const current = vec4(scattering, transmittance);

      // --- temporal reprojection -------------------------------------------------------------
      /*
       * Where was this ray pointing last frame?
       *
       * ## Reproject the direction, not a point on it
       *
       * The previous version reprojected a *point*, at `mix(start, end, 0.5)` — the middle of the
       * shell interval along this ray — on the reasoning that clouds are far away and roughly
       * co-planar, so one representative depth reprojects the whole layer to within a pixel. The
       * reasoning is sound and the choice of depth was not, because `start` and `end` are not a
       * smooth function of screen position:
       *
       *   - `end` has already been clamped by the scene depth, so at a terrain silhouette it jumps
       *     from tens of kilometres to a few hundred metres;
       *   - a ray that misses the shell entirely comes back with `end = −1`, which makes the
       *     midpoint of the interval *negative* — a point behind the eye;
       *   - a ray that enters the layer and leaves through the top has a different interval again.
       *
       * Wherever that jumps, the reprojected coordinate jumps with it and falls outside [0, 1],
       * `onScreen` goes false, and the blend switches off along a hard edge. That is the artefact
       * this layer was switched off for: two hard horizontal edges with a smeared, over-dense band
       * between them — photographed at 11,800 ft over a broken deck before the fix.
       *
       * A direction has none of those cases. `vec4(rayDirection, 0)` reprojects the point at
       * infinity along the ray, which is continuous everywhere, has no dependence on the shell
       * geometry or the scene depth, and is exact for a camera that only rotates. For a camera
       * that translates it is wrong by the parallax of the layer — and that is negligible here:
       * cloud at 5 km with the aircraft moving 2.1 m per frame at 500 kt shifts 0.33 px at
       * 1080p, and a third of a pixel is well inside the half-resolution buffer this writes into.
       *
       * `previousClip.w` is `−z_view` for a direction, so the `w > 0` test still means "in front
       * of the camera" and still rejects the half of the sphere behind it.
       */
      const previousClip = this.previousViewProjection.mul(vec4(rayDirection, 0));
      const previousUv = previousClip.xy.div(previousClip.w).mul(0.5).add(0.5);

      const onScreen = previousUv.x
        .greaterThanEqual(0)
        .and(previousUv.x.lessThanEqual(1))
        .and(previousUv.y.greaterThanEqual(0))
        .and(previousUv.y.lessThanEqual(1))
        .and(previousClip.w.greaterThan(0));

      /*
       * Sampled directly, with **no** {@link toBufferUv}, and that is the second half of the fix.
       *
       * The previous version applied it, on the reasoning that the reprojected coordinate comes out
       * of clip space in the renderer's convention while the history is one of this pass's own
       * buffers. That is the right question and the wrong answer, and the two conventions happen to
       * already agree. The chain, stated once:
       *
       *   - the march builds its ray from `ndc = screenUV · 2 − 1`, and `screenUV.y` is 0 at the
       *     *top* of the buffer, so the ray at buffer row `v` is the ray for true NDC `y = 2v − 1`;
       *   - the composite reads this buffer at `toBufferUv(screenUV)`, i.e. `1 − y`, so buffer row
       *     `v` is displayed at screen position `1 − v`. Consistent with the line above, which is
       *     why the layer is the right way up;
       *   - reprojecting that ray gives true NDC `y = 2v − 1`, hence `previousUv.y = v`.
       *
       * So `previousUv` **is** the buffer coordinate already. Applying the flip made a static
       * camera read the vertically mirrored row every frame and feed it back at 0.9, which is what
       * turned the band between the hard edges into an accumulating smear.
       *
       * The check that settles it in one line: with a still camera the reprojection must be the
       * identity, and `previousUv.y = v` is, while `1 − v` is not. `cloudReprojection.test.ts`
       * pins exactly that, on a CPU port of this algebra.
       */
      const previous = history.sample(previousUv);
      const blend = onScreen.select(this.historyValid.mul(this.temporalBlend), float(0));

      return mix(current, previous, blend);
    });

    return raymarch();
  }

  /**
   * Density at a point, in the layer's own coordinates.
   *
   * The order matters and is the standard one: build the base shape, cut it down to the coverage,
   * apply the vertical gradient, then erode the edges with the detail volume. Eroding before
   * applying coverage would eat the interiors as well as the edges and leave lace instead of
   * cloud.
   */
  private sampleDensity(
    shape: (uv: Node<'vec3'>) => Node<'vec4'>,
    detail: (uv: Node<'vec3'>) => Node<'vec4'>,
    position: Node<'vec3'>,
    heightFraction: Node<'float'>,
  ) {
    const height = clamp(heightFraction, 0, 1);

    /*
     * Sample coordinates: horizontal from the world position, **vertical from the height fraction**.
     *
     * The obvious formulation scales all three components of the planet-centred position by one
     * factor, and it silently produces no cloud at all. The horizontal components are local — the
     * floating origin keeps them inside ±500 m (±10 km when this was written; the threshold came
     * down, which makes the argument twentyfold stronger rather than weaker), so at 9 km per repeat
     * they span a fraction of a unit and sample beautifully. The vertical component is a *planet
     * radius*: 6 371 008 / 9 000 ≈ 708, and
     * the old code then multiplied it by 3.2 to stretch the field, giving a texture coordinate of
     * 2265. Sampler hardware resolves texture coordinates in a fixed-point format whose fractional
     * precision collapses once the integer part is that large, so every fetch along the vertical
     * axis returned effectively the same value and the density field lost the variation the
     * erosion remap needs to clear its threshold. FP64 on the CPU does not care, which is why the
     * CPU port of this same function happily reported densities of 0.35–0.43 for a layer the GPU
     * was rendering as perfectly clear air.
     *
     * Driving the vertical axis from the height fraction instead keeps the coordinate in [0, 0.85]
     * and is the better model anyway: the field should vary over the *layer*, not over the planet,
     * and one repeat across ~900 m of thickness against 9 km horizontally gives cumulus the
     * anisotropy it actually has — billows wider than they are tall, rather than spheres.
     */
    const p = position.add(vec3(this.windOffset)).mul(SHAPE_SAMPLE_SCALE);
    const vertical = height.mul(SHAPE_VERTICAL_SPAN);
    const base = shape(vec3(p.x, vertical, p.z));

    // The three finer Worley octaves, combined into an FBM that erodes the Perlin–Worley base.
    const lowFrequency = base.g.mul(0.625).add(base.b.mul(0.25)).add(base.a.mul(0.125));
    const shaped = clamp(remapNode(base.r, lowFrequency.sub(1), float(1), float(0), float(1)), 0, 1);

    /*
     * Vertical gradient.
     *
     * Cumulus has a hard flat base — the condensation level, where the air first gets cold
     * enough — a bulging middle, and a soft top where it runs out of buoyancy. A stratus deck is
     * nearly uniform. `convection` blends between them, which is what makes an overcast read as
     * a flat lid and a scattered day read as separate towers.
     */
    const cumulus = smoothstep(float(0), float(0.11), height).mul(
      smoothstep(float(1), float(0.62), height),
    );
    const stratus = smoothstep(float(0), float(0.06), height).mul(
      smoothstep(float(1), float(0.88), height),
    );
    const gradient = mix(stratus, cumulus, this.convection);

    /*
     * Coverage, from a *horizontal* slice of the same volume.
     *
     * The `y` component is pinned rather than carried through, and that is the whole point.
     * Sample coordinates here are planet-centred, so `p.y` is about 2265 and `p.x`/`p.z` are
     * near zero and vary by less than a tile across the entire visible layer — feeding all three
     * into the coverage lookup gave a field that was, measurably, constant to two decimal places
     * everywhere, so every cloud had the same coverage and the sky had no weather in it. A fixed
     * slice at the base scale gives clumps about nine kilometres across, which is roughly the
     * size of a real cumulus field's gaps.
     */
    const weather = shape(vec3(p.x, float(0.37), p.z)).r;
    /*
     * The threshold the shaped field is cut against.
     *
     * `1 − coverage` looks obvious and is wrong by an order of magnitude: the field lives in
     * 0.66…0.87, so a threshold sweeping 0…1 spends almost all of its range outside the
     * distribution and cuts nothing. It also confuses two different quantities — an okta is a
     * fraction of the *sky dome*, and what a threshold selects is a fraction of the *horizontal
     * plane*. See `coverage.ts` for the derivation and the measurements.
     */
    const threshold = coverageThresholdNode(this.coverage, weather);
    const withCoverage = clamp(
      remapNode(shaped.mul(gradient), threshold, threshold.add(DENSITY_BAND), float(0), float(1)),
      0,
      1,
    );

    // Detail erosion, strongest at the base where wisps hang below the cloud. Same vertical
    // substitution as the base fetch, and for the same reason — `p.y · 11.5` is 8140.
    const detailSample = detail(vec3(p.x, vertical, p.z).mul(11.5));
    const detailFbm = detailSample.r
      .mul(0.625)
      .add(detailSample.g.mul(0.25))
      .add(detailSample.b.mul(0.125));
    const erosion = mix(detailFbm, float(1).sub(detailFbm), clamp(height.mul(4), 0, 1));

    return clamp(
      remapNode(withCoverage, erosion.mul(0.32), float(1), float(0), float(1)),
      0,
      1,
    ).mul(coverageOpacityNode(this.coverage));
  }

  /** Push the weather into the field. */
  setWeather(weather: WeatherState): void {
    this.coverage.value = weather.coverage;
    this.baseAltitude.value = weather.baseAltitudeM;
    this.layerThickness.value = Math.max(120, weather.thicknessM);
    this.convection.value = weather.convection;
    this.shadow.setLayer(
      weather.coverage,
      weather.baseAltitudeM,
      weather.thicknessM,
      weather.convection,
    );
  }

  setSunDirection(direction: Vector3): void {
    this.sunDirection.value.copy(direction).normalize();
    this.shadow.setSunDirection(direction);
  }

  setSunRadiance(r: number, g: number, b: number): void {
    this.sunRadiance.value.set(r, g, b);
  }

  setAmbient(r: number, g: number, b: number): void {
    this.ambient.value.set(r, g, b);
  }

  /**
   * Advect the layer downwind and re-measure how much cloud the aircraft is in.
   *
   * `updateFrame(alpha)`: yes. Allocation-free.
   */
  updateFrame(dtSeconds: number, weather: WeatherState, altitudeM = 0): void {
    // Cloud moves with the wind at its own altitude, which is faster than the surface wind —
    // see `world/weather.ts`. The layer drifts *with* the wind, so the sample coordinate moves
    // against it.
    const speed = weather.windSpeedMps * 1.9;
    const towardRad = ((weather.windDirectionDeg + 180) * Math.PI) / 180;
    this.driftX -= Math.sin(towardRad) * speed * dtSeconds;
    this.driftZ += Math.cos(towardRad) * speed * dtSeconds;
    this.windOffset.value.set(this.driftX, 0, this.driftZ);
    // The ground shadow reads the same offset, so it drifts with the layer rather than sitting
    // still under a moving sky — which is the single most obvious way a faked cloud shadow gives
    // itself away.
    this.shadow.setWindOffset(this.driftX, this.driftZ);

    /*
     * The immersion term, low-passed.
     *
     * Stepped straight from the density sample it flickers, because a cumulus edge is genuinely
     * ragged at the metre scale and the aircraft crosses that scale in a few milliseconds. A
     * ~0.4 s time constant is short enough that entering cloud still feels abrupt and long enough
     * that the whiteout does not strobe on the way in.
     */
    const target = this.settings.clouds.mode === 'off' ? 0 : this.densityAt(altitudeM);
    const k = Math.min(1, dtSeconds / 0.4);
    this.cloudinessValue += (target - this.cloudinessValue) * k;
  }

  /**
   * Density at the aircraft, on the CPU, from the same volumes the shader marches.
   *
   * The detail-erosion octave is deliberately left out. It costs a second trilinear fetch to move
   * a number that only ever drives a whiteout and a buffet — effects whose whole job is to be felt
   * rather than measured — and the coverage field and vertical gradient are what actually decide
   * whether there is cloud at this altitude at all.
   */
  private densityAt(altitudeM: number): number {
    const shape = this.shapeData;
    if (shape === null || this.noiseReady.value !== 1) return 0;

    const size = this.shapeSize;
    const innerRadius = EARTH_RADIUS_M + (this.baseAltitude.value as number);
    const thickness = this.layerThickness.value as number;
    // Planet-centred, exactly as the shader builds it: the centre sits one radius below the
    // tangent-plane origin, so the aircraft's radius is R + its altitude.
    const radius = EARTH_RADIUS_M + altitudeM;
    const height = (radius - innerRadius) / thickness;
    if (height < -0.05 || height > 1.05) return 0;
    const h = Math.min(1, Math.max(0, height));

    // Same coordinates the shader uses — horizontal from the position, vertical from the height
    // fraction. See the note in `sampleDensity`; the two must agree or the whiteout fires in
    // different places from the cloud you can see.
    const scale = 1 / 9_000;
    const px = this.driftX * scale;
    const pz = this.driftZ * scale;
    const vertical = h * 0.85;

    const baseR = sampleVolume(shape, size, 0, px, vertical, pz);
    const baseG = sampleVolume(shape, size, 1, px, vertical, pz);
    const baseB = sampleVolume(shape, size, 2, px, vertical, pz);
    const baseA = sampleVolume(shape, size, 3, px, vertical, pz);

    const lowFrequency = baseG * 0.625 + baseB * 0.25 + baseA * 0.125;
    const shaped = clamp01(remap(baseR, lowFrequency - 1, 1, 0, 1));

    const cumulus = smoothstepScalar(0, 0.11, h) * smoothstepScalar(1, 0.62, h);
    const stratus = smoothstepScalar(0, 0.06, h) * smoothstepScalar(1, 0.88, h);
    const convection = this.convection.value as number;
    const gradient = stratus + (cumulus - stratus) * convection;

    const weather = sampleVolume(shape, size, 0, px, 0.37, pz);
    const coverage = this.coverage.value as number;
    const threshold = coverageThreshold(coverage, weather);
    const withCoverage = clamp01(remap(shaped * gradient, threshold, threshold + DENSITY_BAND, 0, 1));

    return withCoverage * coverageOpacity(coverage);
  }

  applyQuality(settings: QualitySettings): void {
    this.settings = settings;
    const clouds = settings.clouds;
    this.enabled.value = clouds.mode === 'volumetric' ? 1 : 0;
    // 0.9 keeps ten frames in flight, which is where the noise stops being visible; much more and
    // moving cloud edges leave a trail behind them. See {@link temporalBlend} for why it is 0.
    this.temporalBlend.value = clouds.temporalReprojection ? 0.9 : 0;
    // Clamped to the compile-time bound: asking for more steps than the loop can take would
    // silently shorten the ray instead of improving it.
    this.steps.value = Math.min(MAX_RAYMARCH_STEPS, Math.max(8, clouds.raymarchSteps));
    this.refreshShadowAmount();
  }

  dispose(): void {
    this.shapeTexture.dispose();
    this.detailTexture.dispose();
    this.bayerTexture.dispose();
    this.cloudTarget.dispose();
    this.historyTarget.dispose();
    this.material.dispose();
    this.copyMaterial.dispose();
  }
}

/**
 * State for a fullscreen quad that writes a **four-channel value**, not a picture.
 *
 * `blending = NoBlending` is the load-bearing line, and it is not about blending. Three's
 * `NodeMaterial.setupDiffuseColor` ends with
 *
 * ```js
 * if ( transparent === false && blending === NormalBlending && alphaToCoverage === false ) {
 *   diffuseColor.a.assign( 1.0 );
 * }
 * ```
 *
 * — a sensible optimisation for a material drawing an opaque surface, and fatal for one whose alpha
 * channel carries the layer's *transmittance*. With the defaults, every fragment writes alpha
 * exactly 1.0 no matter what the shader computed, so the composite attenuates the scene by 1 and
 * the cloud contributes only its in-scattering: a faint uniform haze with no cloud in it. The
 * readback that caught this showed real varying radiance in RGB and a perfectly flat 1.0 in alpha,
 * which is the signature to remember. `NoBlending` fails the condition and leaves alpha alone,
 * while also keeping blending off — which is what we want anyway, since the quad covers the target
 * and there is nothing underneath to blend with.
 *
 * Depth testing is off for the same class of reason: these targets carry no depth buffer, and a
 * material that wants to depth-test against one that does not exist is a state mismatch that
 * discards fragments without saying so.
 */
function configureQuadMaterial(material: NodeMaterial, name: string): NodeMaterial {
  material.name = name;
  material.depthTest = false;
  material.depthWrite = false;
  material.blending = NoBlending;
  return material;
}

/**
 * Turn a half-float readback into numbers.
 *
 * `readRenderTargetPixelsAsync` on a `HalfFloatType` target returns the raw 16-bit patterns in a
 * `Uint16Array` — there is no float16 typed array to hand them back in. Read directly, a
 * transmittance of 1.0 reads as 15360 and a scattering of 0.5 as 14336, so a probe that skips this
 * reports a plausible-looking table of nonsense. Passes `Float32Array` and friends straight through
 * so the same probe works if the target's type ever changes.
 */
function decodeHalfFloat(raw: ArrayLike<number>): Float32Array {
  if (!(raw instanceof Uint16Array)) return Float32Array.from(raw as ArrayLike<number>);
  const out = new Float32Array(raw.length);
  for (let i = 0; i < raw.length; i++) {
    const bits = raw[i];
    const sign = bits & 0x8000 ? -1 : 1;
    const exponent = (bits >> 10) & 0x1f;
    const mantissa = bits & 0x3ff;
    if (exponent === 0) out[i] = sign * mantissa * 2 ** -24; // subnormal
    else if (exponent === 31) out[i] = mantissa === 0 ? sign * Infinity : NaN;
    else out[i] = sign * (mantissa + 1024) * 2 ** (exponent - 25);
  }
  return out;
}

/** A cloud buffer: half-float so HDR scattering survives, and no depth, because nothing tests it. */
function makeCloudTarget(name: string): RenderTarget {
  const target = new RenderTarget(1, 1, { depthBuffer: false, type: HalfFloatType });
  target.texture.name = name;
  target.texture.minFilter = LinearFilter;
  target.texture.magFilter = LinearFilter;
  return target;
}

/** `smoothstep`, on the CPU, with the same edge order TSL uses (edge1 may be below edge0). */
function smoothstepScalar(edge0: number, edge1: number, x: number): number {
  const t = clamp01((x - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
}

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

/**
 * Both roots of `t² + 2bt + k = 0`, computed without catastrophic cancellation.
 *
 * The naive `−b ± √(b² − k)` loses almost every significant digit in one of its two roots whenever
 * `|k| ≪ b²`, which is exactly the regime here: `b` is a planet radius times a direction cosine and
 * `k` is a few kilometres times a planet diameter. The standard remedy is to compute only the root
 * whose terms *add* — `q = −b − sign(b)·√(b² − k)` — and recover the other from the fact that the
 * product of the roots is `k`.
 *
 * **Not `sign(b)`, though**, and that is a bug fix rather than a stylistic quibble. `sign(0)` is
 * zero, so at `b = 0` the expression collapses to `q = 0`; the divide-by-zero guard then hands back
 * `k / 1e-4` and the pair comes out `{−10¹⁴, 0}` instead of `{−101 km, +101 km}`. `b` is
 * `origin · direction`, which is *exactly* zero for a level ray from directly above the tangent
 * origin — where the camera sits, by construction, on the first frames after every spawn and every
 * floating-origin rebase. At `b = 0` there is no root to prefer, both being `±√(−k)`, so treating
 * zero as positive is safe and the branches agree in the limit from either side.
 *
 * `hit` is load-bearing. When `b² < k` the ray misses the sphere, and the roots do **not** then
 * collapse to `−b`: the discriminant clamps to zero so `q = −b`, but `other = k/(−b)` is a
 * different number entirely, and the pair is meaningless rather than empty. Callers must consult
 * the flag; assuming an `end > start` test will reject a missed sphere is what cost the far half of
 * the cloud deck from cruise altitude. All three of these are pinned by `shellInterval.test.ts`,
 * against an f32-faithful CPU port of this exact function.
 */
function shellRoots(
  b: Node<'float'>,
  k: Node<'float'>,
): { near: Node<'float'>; far: Node<'float'>; hit: Node<'bool'> } {
  const discriminant = b.mul(b).sub(k).toVar();
  const s = sqrt(max(discriminant, float(0)));
  const q = b.negate().sub(b.lessThan(0).select(s.negate(), s)).toVar();
  const other = k.div(q.abs().lessThan(1e-4).select(float(1e-4), q));
  return { near: min(q, other), far: max(q, other), hit: discriminant.greaterThan(0) };
}

/** `remap`, as a node. Same function as `cloudNoise.ts`'s, evaluated on the GPU. */
function remapNode(
  value: Node<'float'>,
  low1: Node<'float'>,
  high1: Node<'float'>,
  low2: Node<'float'>,
  high2: Node<'float'>,
) {
  return low2.add(value.sub(low1).mul(high2.sub(low2)).div(max(high1.sub(low1), float(1e-5))));
}

/** Henyey–Greenstein phase function, normalised over the sphere. */
function henyeyGreenstein(cosTheta: Node<'float'>, g: number) {
  const g2 = g * g;
  return float((1 - g2) / (4 * Math.PI)).div(
    pow(max(float(1 + g2).sub(cosTheta.mul(2 * g)), float(1e-3)), float(1.5)),
  );
}

function makeVolume(
  data: Uint8Array,
  size: number,
  format: typeof RGBAFormat | typeof RedFormat,
  width = size,
  height = size,
  depth = size,
): Data3DTexture {
  const volume = new Data3DTexture(data, width, height, depth);
  volume.format = format;
  volume.minFilter = LinearFilter;
  volume.magFilter = LinearFilter;
  // Repeat on every axis: the field is tileable by construction (see cloudNoise.ts) and it is
  // tiled hundreds of times across the visible layer.
  volume.wrapS = RepeatWrapping;
  volume.wrapT = RepeatWrapping;
  volume.wrapR = RepeatWrapping;
  volume.generateMipmaps = false;
  volume.needsUpdate = true;
  return volume;
}
